import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import ManagementHeader from './ManagementHeader';
import MgField from './MgField';
import { fetchEquipmentDetailForManagement, updateEquipment, deleteEquipment, fetchFactoryLines } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import { EQUIPMENT_STATUS_KO, EQUIPMENT_STATUS_BADGE } from './equipmentStatus';
import './management.css';

const DASH = '—';

function EquipmentDetailPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { equipmentId } = useParams();
    const [searchParams] = useSearchParams();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [originalData, setOriginalData] = useState(null);
    const [lines, setLines] = useState([]);

    // URL 쿼리 파라미터에서 edit 모드 확인
    useEffect(() => {
        const editMode = searchParams.get('edit');
        if (editMode === 'true') {
            setIsEditing(true);
        } else {
            setIsEditing(false);
        }
    }, [searchParams]);

    const [formData, setFormData] = useState({
        id: equipmentId,
        equipmentCode: '',
        equipmentName: '',
        model: '',
        line: '',
        lineName: '',
        factory: '',
        factoryName: '',
        factoryId: null,
        lineId: null,
        serialNumber: '',
        manufacturer: '',
        installDate: '',
        warrantyEndDate: '',
        status: 'IDLE',
        maxTemperature: null,
        maxPressure: null,
        ratedPower: null,
        targetCycleTime: null,
        cavityCount: null,
        lastInspectionDate: '',
        nextMaintenanceDate: '',
        description: '',
        equipmentType: ''
    });

    // API에서 설비 데이터 가져오기
    useEffect(() => {
        const loadEquipmentDetail = async () => {
            try {
                setLoading(true);
                const data = await fetchEquipmentDetailForManagement(equipmentId);


                const transformedData = {
                    id: data.id,
                    equipmentCode: data.equipmentCode || '',
                    equipmentName: data.equipmentName || '',
                    model: data.model || '',
                    lineName: data.lineName || '-',
                    factoryName: data.factoryName || '-',
                    factoryId: data.factoryId,
                    lineId: data.lineId,
                    serialNumber: data.serialNumber || '-',
                    manufacturer: data.manufacturer || '-',
                    installDate: data.installDate || '',
                    warrantyEndDate: data.warrantyEndDate || '',
                    status: data.status || 'IDLE',
                    maxTemperature: data.maxTemperature || null,
                    maxPressure: data.maxPressure || null,
                    ratedPower: data.ratedPower || null,
                    targetCycleTime: data.targetCycleTime || null,
                    cavityCount: data.cavityCount || null,
                    lastInspectionDate: data.lastInspectionDate || '',
                    nextMaintenanceDate: data.nextMaintenanceDate || '',
                    description: data.description || '',
                    equipmentType: data.equipmentType || ''
                };

                setFormData(transformedData);
                setOriginalData(transformedData);
            } catch (err) {
                console.error('설비 상세 정보 조회 실패:', err);
                alert(t('pages.management.equipmentDetail.alertLoadFail'));
            } finally {
                setLoading(false);
            }
        };

        if (equipmentId) {
            loadEquipmentDetail();
        }
    }, [equipmentId]);

    // 소속 공장의 라인 목록 로드 (라인 변경 드롭다운용)
    useEffect(() => {
        if (!formData.factoryId) {
            setLines([]);
            return;
        }

        const loadLines = async () => {
            try {
                const data = await fetchFactoryLines(formData.factoryId);
                setLines(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error('라인 목록 조회 실패:', err);
                setLines([]);
            }
        };

        loadLines();
    }, [formData.factoryId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            // lineId는 백엔드가 숫자 ID를 요구하므로 빈 값은 null로 전달
            [name]: name === 'lineId' ? (value ? parseInt(value, 10) : null) : value
        }));
    };

    const handleEdit = () => {
        // URL에 ?edit=true 쿼리 파라미터 추가
        navigate(`/equipment/${equipmentId}?edit=true`, { replace: true });
    };

    const handleCancel = () => {
        if (isEditing) {
            // 원래 데이터로 복원
            setFormData(originalData);
            // URL에서 ?edit=true 제거
            navigate(`/equipment/${equipmentId}`, { replace: true });
        } else {
            navigate('/equipment-list');
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            // API 요청 데이터 구성
            const updateData = {
                factoryId: formData.factoryId,
                lineId: formData.lineId,
                equipmentCode: formData.equipmentCode,
                equipmentName: formData.equipmentName,
                model: formData.model,
                manufacturer: formData.manufacturer,
                serialNumber: formData.serialNumber,
                equipmentType: formData.equipmentType,
                installDate: formData.installDate || null,
                warrantyEndDate: formData.warrantyEndDate || null,
                maxTemperature: formData.maxTemperature,
                maxPressure: formData.maxPressure,
                ratedPower: formData.ratedPower,
                targetCycleTime: formData.targetCycleTime,
                cavityCount: formData.cavityCount,
                status: formData.status,
                productName: null,
                currentMoldId: null,
                specifications: null,
                lastInspectionDate: formData.lastInspectionDate || null,
                nextMaintenanceDate: formData.nextMaintenanceDate || null,
                description: formData.description
            };


            await updateEquipment(equipmentId, updateData);

            alert(t('pages.management.equipmentDetail.alertUpdateSuccess'));

            // 저장 후 데이터 다시 로드
            const updatedData = await fetchEquipmentDetailForManagement(equipmentId);
            const transformedData = {
                id: updatedData.id,
                equipmentCode: updatedData.equipmentCode || '',
                equipmentName: updatedData.equipmentName || '',
                model: updatedData.model || '',
                lineName: updatedData.lineName || '-',
                factoryName: updatedData.factoryName || '-',
                factoryId: updatedData.factoryId,
                lineId: updatedData.lineId,
                serialNumber: updatedData.serialNumber || '-',
                manufacturer: updatedData.manufacturer || '-',
                installDate: updatedData.installDate || '',
                warrantyEndDate: updatedData.warrantyEndDate || '',
                status: updatedData.status || 'IDLE',
                maxTemperature: updatedData.maxTemperature || null,
                maxPressure: updatedData.maxPressure || null,
                ratedPower: updatedData.ratedPower || null,
                targetCycleTime: updatedData.targetCycleTime || null,
                cavityCount: updatedData.cavityCount || null,
                lastInspectionDate: updatedData.lastInspectionDate || '',
                nextMaintenanceDate: updatedData.nextMaintenanceDate || '',
                description: updatedData.description || '',
                equipmentType: updatedData.equipmentType || ''
            };

            setFormData(transformedData);
            setOriginalData(transformedData);

            // 읽기 모드로 전환 (URL도 변경)
            navigate(`/equipment/${equipmentId}`, { replace: true });
        } catch (error) {
            console.error('설비 정보 업데이트 실패:', error);
            alert(t('pages.management.equipmentDetail.alertUpdateFail'));
        }
    };

    const handleDelete = async () => {
        if (window.confirm(t('pages.management.equipmentDetail.confirmDelete'))) {
            try {
                await deleteEquipment(equipmentId);
                alert(t('pages.management.equipmentDetail.alertDeleteSuccess'));
                navigate('/equipment-list');
            } catch (error) {
                console.error('설비 삭제 실패:', error);
                alert(t('pages.management.equipmentDetail.alertDeleteFail'));
            }
        }
    };

    /* status 는 백엔드 코드(RUNNING…) — 한글 키로 바꿔 라벨·배지 색을 고른다 */
    const statusKo = EQUIPMENT_STATUS_KO[formData.status] || '대기';
    const getStatusLabel = (koreanKey) => {
        const map = {
            '가동': t('pages.management.equipmentDetail.statusOption.running'),
            '대기': t('pages.management.equipmentDetail.statusOption.idle'),
            '정지': t('pages.management.equipmentDetail.statusOption.stopped'),
            '점검': t('pages.management.equipmentDetail.statusOption.maintenance'),
            '고장': t('pages.management.equipmentDetail.statusOption.fault'),
            '오프라인': t('pages.management.equipmentDetail.statusOption.offline'),
        };
        return map[koreanKey] || koreanKey;
    };

    const L = (key) => t(`pages.management.equipmentDetail.label.${key}`);
    /* 조회 모드 값: 비어 있거나 '-' 이면 — */
    const show = (v) => (v == null || v === '' || v === '-' ? DASH : v);
    const unit = (v, u) => (v ? `${v}${u}` : DASH);
    const cycle = formData.targetCycleTime ? `${(formData.targetCycleTime / 1000).toFixed(1)}s` : DASH;
    const value = (v) => <div className="mg-value">{show(v)}</div>;

    if (loading) {
        return (
            <MainLayout>
                <div className="mg-page">
                    <div className="mg-loading">{t('pages.management.equipmentDetail.loading')}</div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="mg-page">
                <ManagementHeader
                    backTo="/equipment-list"
                    crumbs={[
                        { label: t('sidebar.management.equipmentList'), to: '/equipment-list' },
                        { label: t('pages.management.equipmentDetail.title') }
                    ]}
                >
                    {!isEditing ? (
                        <>
                            <button type="button" className="mg-btn danger" onClick={handleDelete}>
                                <span className="material-symbols-outlined">delete</span>
                                {t('pages.management.equipmentDetail.btn.delete')}
                            </button>
                            <button type="button" className="mg-btn primary" onClick={handleEdit}>
                                <span className="material-symbols-outlined">edit</span>
                                {t('pages.management.equipmentDetail.btn.edit')}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="mg-btn secondary" onClick={handleCancel}>
                                <span className="material-symbols-outlined">close</span>
                                {t('pages.management.equipmentDetail.btn.cancel')}
                            </button>
                            <button type="button" className="mg-btn primary" onClick={handleSave}>
                                <span className="material-symbols-outlined">check</span>
                                {t('pages.management.equipmentDetail.btn.save')}
                            </button>
                        </>
                    )}
                </ManagementHeader>

                {/* 프로필 카드 */}
                <div className="mg-panel mg-profile">
                    <span className="mg-profile-icon"><span className="material-symbols-outlined">precision_manufacturing</span></span>
                    <div className="mg-profile-info">
                        <h2 className="mg-profile-name">{show(formData.equipmentName)}</h2>
                        <p className="mg-profile-sub">{show(formData.model)}</p>
                        <span className={`mg-badge ${EQUIPMENT_STATUS_BADGE[statusKo] || 'neutral'}`}>{getStatusLabel(statusKo)}</span>
                    </div>
                    <div className="mg-stats">
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('cavity')}</span>
                            <span className="mg-stat-value">{show(formData.cavityCount)}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('targetCycle')}</span>
                            <span className="mg-stat-value">{cycle}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('installDate')}</span>
                            <span className="mg-stat-value">{show(formData.installDate)}</span>
                        </div>
                        <div className="mg-stat">
                            <span className="mg-stat-label">{L('lastInspection')}</span>
                            <span className="mg-stat-value">{show(formData.lastInspectionDate)}</span>
                        </div>
                    </div>
                </div>

                <form className={`mg-form ${isEditing ? 'editing' : ''}`} onSubmit={handleSave}>
                    {/* 기본 정보 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.equipmentDetail.section.basic')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('equipmentName')}>
                                {isEditing
                                    ? <input type="text" name="equipmentName" className="mg-input" value={formData.equipmentName} onChange={handleInputChange} required />
                                    : value(formData.equipmentName)}
                            </MgField>
                            <MgField label={L('equipmentCode')}>{value(formData.equipmentCode)}</MgField>
                            <MgField label={L('model')}>
                                {isEditing
                                    ? <input type="text" name="model" className="mg-input" value={formData.model} onChange={handleInputChange} required />
                                    : value(formData.model)}
                            </MgField>
                            <MgField label={L('factory')}>{value(formData.factoryName)}</MgField>
                            <MgField label={L('line')}>
                                {isEditing ? (
                                    <select name="lineId" className="mg-select" value={formData.lineId ?? ''} onChange={handleInputChange}>
                                        <option value="">{t('pages.management.equipmentDetail.placeholder.lineSelect')}</option>
                                        {lines.map(line => (
                                            <option key={line.id} value={line.id}>{line.lineName}</option>
                                        ))}
                                    </select>
                                ) : value(formData.lineName)}
                            </MgField>
                            <MgField label={L('manufacturer')}>{value(formData.manufacturer)}</MgField>
                            <MgField label={L('serialNumber')}>{value(formData.serialNumber)}</MgField>
                            <MgField label={L('installDate')}>{value(formData.installDate)}</MgField>
                            <MgField label={L('warrantyEndDate')}>{value(formData.warrantyEndDate)}</MgField>
                            <MgField label={L('status')}>
                                {isEditing ? (
                                    <select name="status" className="mg-select" value={formData.status} onChange={handleInputChange} required>
                                        <option value="RUNNING">{t('pages.management.equipmentDetail.statusOption.running')}</option>
                                        <option value="IDLE">{t('pages.management.equipmentDetail.statusOption.idle')}</option>
                                        <option value="STOPPED">{t('pages.management.equipmentDetail.statusOption.stopped')}</option>
                                        <option value="MAINTENANCE">{t('pages.management.equipmentDetail.statusOption.maintenance')}</option>
                                        <option value="FAULT">{t('pages.management.equipmentDetail.statusOption.fault')}</option>
                                        <option value="OFFLINE">{t('pages.management.equipmentDetail.statusOption.offline')}</option>
                                    </select>
                                ) : value(getStatusLabel(statusKo))}
                            </MgField>
                        </div>
                    </div>

                    {/* 운영 사양 */}
                    <div className="mg-panel">
                        <h3 className="mg-panel-title">{t('pages.management.equipmentDetail.section.spec')}</h3>
                        <div className="mg-grid">
                            <MgField label={L('maxTemperature')}>{value(unit(formData.maxTemperature, '°C'))}</MgField>
                            <MgField label={L('maxPressure')}>{value(unit(formData.maxPressure, ' MPa'))}</MgField>
                            <MgField label={L('ratedPower')}>{value(unit(formData.ratedPower, ' kW'))}</MgField>
                            <MgField label={L('targetCycleTime')}>{value(cycle)}</MgField>
                            <MgField label={L('cavityCount')}>{value(formData.cavityCount)}</MgField>
                            <MgField label={L('lastInspectionDate')}>{value(formData.lastInspectionDate)}</MgField>
                            <MgField label={L('nextMaintenanceDate')}>{value(formData.nextMaintenanceDate)}</MgField>
                            <MgField label={L('description')} full>
                                {isEditing
                                    ? <textarea name="description" className="mg-textarea" value={formData.description || ''} onChange={handleInputChange} rows="4" />
                                    : value(formData.description)}
                            </MgField>
                        </div>
                    </div>

                    {isEditing && (
                        <div className="mg-form-actions">
                            <button type="button" className="mg-btn secondary" onClick={handleCancel}>{t('pages.management.equipmentDetail.btn.cancel')}</button>
                            <button type="submit" className="mg-btn primary">{t('pages.management.equipmentDetail.btn.save')}</button>
                        </div>
                    )}
                </form>
            </div>
        </MainLayout>
    );
}

export default EquipmentDetailPage;
